/**
 * Guest Estimate Modal
 *
 * Bootstrap 5 modal for guest users to save their estimate.
 * Collects lead/marketing data, validates with Turnstile,
 * saves to database, and optionally submits to Pardot.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Loader2, Copy, ExternalLink } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import { useToast } from '../../context/ToastContext';
import { GuestEstimateFormData } from '../../types';
import { fallbackGeo, regionsFor, regionLabelFor, GeoCountry } from '../../data/regions';
import { fetchUserCategories } from '../../services/userCategoriesApi';
import { UserCategory, USER_CATEGORY_FALLBACK } from '../../data/userCategories';
import { formatPhoneNumber, isCompletePhoneNumber } from '../../utils/phoneMask';
import { TURNSTILE_CONFIG } from '../../config/integrations';
import { submitGuestEstimate, fetchGeoCountries, AddressDetail } from '../../services/api';
import { AddressAutocomplete } from './AddressAutocomplete';
import { trackProjectPlannerSubmitted, SaveTrigger } from '../../utils/gtmTracking';
import { reportClientError } from '../../utils/errorReporting';
import { captureUtmParams } from '../../utils/utmParams';
import './GuestEstimateModal.css';

interface GuestEstimateModalProps {
  isOpen: boolean;
  /** Analytics only: how the prompt was opened. */
  saveTrigger?: SaveTrigger;
  onClose: () => void;
  onSuccess?: (guid: string) => void;
}

const STORAGE_KEY = 'trusscore_guest_form';

const INITIAL_FORM: GuestEstimateFormData = {
  projectName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  userCategory: '',
  projectType: '',
  country: '',
  region: '',
  city: '',
  postalCode: '',
  addressOne: '',
  addressTwo: '',
  turnstileToken: '',
};

/** Load previously used form values from localStorage (excludes projectName) */
function loadSavedFormValues(): Partial<GuestEstimateFormData> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const saved = JSON.parse(raw);
    delete saved.projectName;
    delete saved.turnstileToken;
    return saved;
  } catch {
    return {};
  }
}

/** Persist form values to localStorage (excludes projectName and turnstileToken) */
function saveFormValues(form: GuestEstimateFormData): void {
  try {
    const toSave = { ...form };
    delete (toSave as any).projectName;
    delete (toSave as any).turnstileToken;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch { /* ignore */ }
}

export const GuestEstimateModal: React.FC<GuestEstimateModalProps> = ({
  isOpen,
  saveTrigger,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation('results');
  const { state } = useEstimator();
  const { settings: appSettings } = useAppSettings();
  const { partner } = usePartnerBranding();
  const { showToast } = useToast();
  const [form, setForm] = useState<GuestEstimateFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof GuestEstimateFormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  // Country/region list — admin-managed (hydrated from /api/geo.php), with the
  // hardcoded list in regions.ts as the offline fallback.
  const [geo, setGeo] = useState<GeoCountry[]>(fallbackGeo);
  // "I Am A" options — admin-managed (hydrated from /api/user-categories.php),
  // with the hardcoded list as the offline fallback.
  const [categories, setCategories] = useState<UserCategory[]>(USER_CATEGORY_FALLBACK);

  // Fade in/out state — stays mounted during close animation
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const turnstileActive = TURNSTILE_CONFIG.enabled && appSettings.turnstileEnabled;

  // Hydrate the admin-managed country/region list once (falls back silently).
  useEffect(() => {
    let cancelled = false;
    // Category and country names are admin-managed text translated
    // server-side, so a language switch has to re-fetch them.
    fetchGeoCountries().then((g) => { if (!cancelled && g) setGeo(g); });
    fetchUserCategories().then((c) => { if (!cancelled && c) setCategories(c); });
    return () => { cancelled = true; };
  }, [i18n.language]);

  // Pre-fill from localStorage + wizard state when modal opens
  useEffect(() => {
    if (isOpen) {
      const saved = loadSavedFormValues();
      setForm(prev => ({
        ...INITIAL_FORM,
        ...saved,
        projectName: state.projectInfo?.projectName || '',
        turnstileToken: '',
      }));
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, state.projectInfo?.projectName]);

  // Render Turnstile widget — depends on `mounted` so the effect re-runs
  // once the DOM is actually rendered (turnstileRef.current becomes non-null).
  useEffect(() => {
    if (!isOpen || !mounted || !turnstileActive || !turnstileRef.current) return;

    const renderWidget = () => {
      if (turnstileWidgetId.current !== null) {
        (window as any).turnstile?.reset(turnstileWidgetId.current);
        return;
      }

      if ((window as any).turnstile && turnstileRef.current) {
        turnstileWidgetId.current = (window as any).turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_CONFIG.siteKey,
          callback: (token: string) => {
            setForm(prev => ({ ...prev, turnstileToken: token }));
          },
          'expired-callback': () => {
            setForm(prev => ({ ...prev, turnstileToken: '' }));
          },
          // A failed challenge used to vanish silently; now it's an error report.
          'error-callback': (code: string) => {
            setForm(prev => ({ ...prev, turnstileToken: '' }));
            reportClientError({
              scope: 'turnstile',
              error: new Error(`Turnstile widget error ${code}`),
              extra: { turnstile_stage: 'widget', turnstile_code: String(code), turnstile_form: 'project_planner' },
            });
            return true; // handled — keep Cloudflare from logging it as unhandled
          },
          theme: 'light',
        });
      }
    };

    if ((window as any).turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector('script[src*="turnstile"]');
      if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => setTimeout(renderWidget, 100);
        script.onerror = () => reportClientError({
          scope: 'turnstile',
          error: new Error('Turnstile script failed to load'),
          extra: { turnstile_stage: 'script', turnstile_form: 'project_planner' },
        });
        document.head.appendChild(script);
      } else {
        const checkReady = setInterval(() => {
          if ((window as any).turnstile) {
            clearInterval(checkReady);
            renderWidget();
          }
        }, 100);
        return () => clearInterval(checkReady);
      }
    }
  }, [isOpen, mounted, turnstileActive]);

  // Cleanup widget on close
  useEffect(() => {
    if (!isOpen && turnstileWidgetId.current !== null) {
      try {
        (window as any).turnstile?.remove(turnstileWidgetId.current);
      } catch { /* ignore */ }
      turnstileWidgetId.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Delay adding 'show' class so the browser registers the initial state first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (mounted) {
      // Start fade out
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 300); // match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleChange = (field: keyof GuestEstimateFormData, value: string) => {
    if (field === 'phone') {
      value = formatPhoneNumber(value);
    }

    setForm(prev => {
      const updated = { ...prev, [field]: value };
      // Classic mode only: changing the country select resets the dependent
      // region select. In autocomplete mode country is free text, so editing
      // it must not wipe a resolved region.
      if (field === 'country' && appSettings.addressInputMode !== 'autocomplete') {
        updated.region = '';
      }
      return updated;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Apply a resolved autocomplete address as one merge (no per-field churn /
  // country→region reset). Manual edits afterward still go through handleChange.
  const applyResolvedAddress = (detail: AddressDetail) => {
    setForm(prev => ({
      ...prev,
      addressOne: detail.addressOne || prev.addressOne,
      city: detail.city || prev.city,
      region: detail.region || prev.region,
      postalCode: detail.postalCode || prev.postalCode,
      country: detail.country || prev.country,
    }));
    setErrors(prev => ({ ...prev, addressOne: undefined, city: undefined, region: undefined, postalCode: undefined, country: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof GuestEstimateFormData, string>> = {};

    if (!form.projectName.trim()) newErrors.projectName = t('lead.errors.projectName');
    if (!form.firstName.trim()) newErrors.firstName = t('lead.errors.firstName');
    if (!form.lastName.trim()) newErrors.lastName = t('lead.errors.lastName');
    if (!form.email.trim()) {
      newErrors.email = t('lead.errors.email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t('save.errors.emailInvalid');
    }
    if (!form.phone.trim()) {
      newErrors.phone = t('lead.errors.phone');
    } else if (!isCompletePhoneNumber(form.phone)) {
      newErrors.phone = t('lead.errors.phoneIncomplete');
    }
    if (!form.userCategory) newErrors.userCategory = t('lead.errors.category');
    if (!form.projectType) newErrors.projectType = t('lead.errors.projectType');
    if (autocompleteMode) {
      if (!form.addressOne.trim()) newErrors.addressOne = t('lead.errors.address');
      if (!form.country.trim()) newErrors.country = t('lead.errors.country');
      // region/territory optional in autocomplete mode (some addresses lack a state).
    } else {
      if (!form.country) newErrors.country = t('account:info.errors.country');
      // regionLabel is admin-managed DB text ("State"/"Province") — Phase 3.
      if (!form.region) newErrors.region = t('account:info.errors.region', { region: regionLabelFor(geo, form.country).toLowerCase() });
    }
    if (!form.city.trim()) newErrors.city = t('lead.errors.city');
    if (!form.postalCode.trim()) newErrors.postalCode = t('lead.errors.postalCode');

    if (turnstileActive && !form.turnstileToken) {
      newErrors.turnstileToken = t('save.errors.verification');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setSubmitting(true);

    try {
      const result = await submitGuestEstimate({
        formData: form,
        estimateData: state.estimateResult!,
        wizardState: {
          calculationMode: state.calculationMode,
          projectInfo: state.projectInfo,
          wallsConfig: state.wallsConfig,
          ceilingConfig: state.ceilingConfig,
          dimensionsConfig: state.dimensionsConfig,
          applicationConfig: state.applicationConfig,
          individualWalls: state.individualWalls,
          currency: state.currency,
          completedSteps: state.completedSteps,
          currentStep: state.currentStep,
        },
        // Stamp the branded portal this estimate was created in, so its share
        // link re-applies the partner branding (null on apex/Trusscore).
        retailerId: partner?.id ?? null,
        // Standard UTM params captured at landing (folds in any present now).
        utm: captureUtmParams(),
      });

      setSubmitSuccess(result.guid);
      saveFormValues(form);
      // Fire GTM conversion event only after confirmed API success, never on
      // click — Turnstile failures / network errors would corrupt Google Ads
      // bidding signals.
      if (state.estimateResult) {
        trackProjectPlannerSubmitted(
          state.calculationMode,
          state.applicationConfig.spaceType,
          result.guid,
          state.currency,
          state.estimateResult,
          {
            userType: form.userCategory,
            projectCategory: form.projectType,
            projectName: form.projectName,
            country: form.country,
            region: form.region,
            city: form.city,
          },
          { captureMode: 'project_planner', saveTrigger: saveTrigger ?? null },
        );
      }
      // Pardot debug log — only when admin has pardotDebug on AND the server
      // returned the payload. Shape matches what would be POSTed to the form
      // handler so it can be reviewed before flipping Pardot live.
      if (appSettings.pardotDebug && result.pardotDebug) {
        console.log('[Pardot debug payload]', result.pardotDebug);
      }
      onSuccess?.(result.guid);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('save.errors.failed');
      // The server's own Turnstile rejection surfaces inline; report it too.
      if (/verification/i.test(message)) {
        reportClientError({
          scope: 'turnstile',
          error: err,
          extra: { turnstile_stage: 'server', turnstile_form: 'project_planner' },
        });
      }
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!mounted) return null;

  const regions = regionsFor(geo, form.country);
  const regionLabel = regionLabelFor(geo, form.country);
  // Admin-toggled: 'autocomplete' swaps the country/region selects for a full
  // address typeahead (Address One/Two, Territory). Falls back to manual entry
  // when no API key is configured (the proxy just returns no suggestions).
  const autocompleteMode = appSettings.addressInputMode === 'autocomplete';

  return (
    <div
      className={`modal d-block gem-backdrop fade ${visible ? 'show' : ''}`}
      tabIndex={-1}
      style={{ transition: 'opacity 0.3s ease' }}
    >
      <div className={`modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable ${visible ? 'gem-dialog-animate' : ''}`}>
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header gem-header">
            <h5 className="modal-title fw-bold">{t('lead.title')}</h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label={t('auth:close')} />
          </div>

          {/* Success State */}
          {submitSuccess ? (
            <div className="modal-body text-center py-5">
              <div className="text-success mb-3">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="fw-bold mb-2">{t('lead.success.title')}</h3>
              <p className="text-muted mb-3">{t('lead.success.body')}</p>
              <div className="bg-light rounded p-2 mb-3 d-flex align-items-center justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-link p-1 text-secondary flex-shrink-0"
                  title={t('lead.success.copyTitle')}
                  aria-label={t('lead.success.copyTitle')}
                  onClick={() => {
                    const url = `${window.location.origin}/estimate/${submitSuccess}`;
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(url);
                    } else {
                      const textarea = document.createElement('textarea');
                      textarea.value = url;
                      textarea.style.position = 'fixed';
                      textarea.style.opacity = '0';
                      document.body.appendChild(textarea);
                      textarea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textarea);
                    }
                    showToast(t('lead.success.copied'), undefined, 'success');
                  }}
                >
                  <Copy size={16} />
                </button>
                <code
                  className="text-secondary"
                  style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minWidth: 0,
                  }}
                >
                  {window.location.origin}/estimate/{submitSuccess}
                </code>
              </div>
              <div className="d-flex justify-content-center gap-2">
                <a
                  href={`${window.location.origin}/estimate/${submitSuccess}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                >
                  <ExternalLink size={16} />
                  {t('lead.success.view')}
                </a>
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                  {t('auth:close')}
                </button>
              </div>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} noValidate>
              <div className="modal-body">
                {submitError && (
                  <div className="alert alert-danger py-2">{submitError}</div>
                )}

                {/* Project Name */}
                <div className="mb-3">
                  <label htmlFor="gem-projectName" className="form-label fw-semibold">
                    {t('lead.fields.projectName')} <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="gem-projectName"
                    className={`form-control ${errors.projectName ? 'is-invalid' : ''}`}
                    value={form.projectName}
                    onChange={e => handleChange('projectName', e.target.value)}
                    placeholder={t('lead.placeholders.projectName')}
                  />
                  {errors.projectName && <div className="invalid-feedback">{errors.projectName}</div>}
                </div>

                {/* Name Row */}
                <div className="row mb-3">
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                    <label htmlFor="gem-firstName" className="form-label fw-semibold">
                      {t('auth:fields.firstName')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="gem-firstName"
                      className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                      value={form.firstName}
                      onChange={e => handleChange('firstName', e.target.value)}
                      placeholder={t('lead.placeholders.firstName')}
                    />
                    {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label htmlFor="gem-lastName" className="form-label fw-semibold">
                      {t('auth:fields.lastName')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="gem-lastName"
                      className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                      value={form.lastName}
                      onChange={e => handleChange('lastName', e.target.value)}
                      placeholder={t('lead.placeholders.lastName')}
                    />
                    {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                  </div>
                </div>

                {/* Email / Phone Row */}
                <div className="row mb-3">
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                    <label htmlFor="gem-email" className="form-label fw-semibold">
                      {t('save.emailLabel')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      id="gem-email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      value={form.email}
                      onChange={e => handleChange('email', e.target.value)}
                      placeholder={t('lead.placeholders.email')}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label htmlFor="gem-phone" className="form-label fw-semibold">
                      {t('lead.fields.phone')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      id="gem-phone"
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      value={form.phone}
                      onChange={e => handleChange('phone', e.target.value)}
                      placeholder="(###) ###-####"
                    />
                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                  </div>
                </div>

                {/* User Category / Project Type Row */}
                <div className="row mb-3">
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                    <label htmlFor="gem-userCategory" className="form-label fw-semibold">
                      {t('account:info.iAmA')} <span className="text-danger">*</span>
                    </label>
                    <select
                      id="gem-userCategory"
                      className={`form-select ${errors.userCategory ? 'is-invalid' : ''}`}
                      value={form.userCategory}
                      onChange={e => handleChange('userCategory', e.target.value)}
                    >
                      <option value="" disabled>{t('account:info.select')}</option>
                      {/* value is the ENGLISH name — it is submitted, stored on
                          the estimate, and sent to Pardot, so it must not vary
                          by the visitor's language. Only the label translates. */}
                      {categories.map(cat => (
                        <option key={cat.slug} value={cat.nameEn}>{cat.name}</option>
                      ))}
                    </select>
                    {errors.userCategory && <div className="invalid-feedback">{errors.userCategory}</div>}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label fw-semibold">
                      {t('save.projectIs')} <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex flex-column gap-2 pt-1">
                      <div className="form-check">
                        <input
                          type="radio"
                          id="gem-commercial"
                          name="projectType"
                          value="commercial"
                          className="form-check-input"
                          checked={form.projectType === 'commercial'}
                          onChange={e => handleChange('projectType', e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="gem-commercial">{t('lead.projectTypes.commercial')}</label>
                      </div>
                      <div className="form-check">
                        <input
                          type="radio"
                          id="gem-residential"
                          name="projectType"
                          value="residential"
                          className="form-check-input"
                          checked={form.projectType === 'residential'}
                          onChange={e => handleChange('projectType', e.target.value)}
                        />
                        <label className="form-check-label" htmlFor="gem-residential">{t('lead.projectTypes.residential')}</label>
                      </div>
                    </div>
                    {errors.projectType && <small className="text-danger">{errors.projectType}</small>}
                  </div>
                </div>

                {autocompleteMode ? (
                  <AddressAutocomplete
                    values={form}
                    errors={errors}
                    regionLabel={regionLabel}
                    onChange={handleChange}
                    onResolve={applyResolvedAddress}
                    nudgeIncomplete={appSettings.addressNudgeIncomplete}
                  />
                ) : (
                <>
                {/* Country / Region Row */}
                <div className="row mb-3">
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                    <label htmlFor="gem-country" className="form-label fw-semibold">
                      {t('account:info.country')} <span className="text-danger">*</span>
                    </label>
                    <select
                      id="gem-country"
                      className={`form-select ${errors.country ? 'is-invalid' : ''}`}
                      value={form.country}
                      onChange={e => handleChange('country', e.target.value)}
                    >
                      <option value="">{t('lead.selectCountry')}</option>
                      {geo.map(c => (
                        <option key={c.isoCode || c.nameEn} value={c.nameEn}>{c.name}</option>
                      ))}
                    </select>
                    {errors.country && <div className="invalid-feedback">{errors.country}</div>}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label htmlFor="gem-region" className="form-label fw-semibold">
                      {regionLabel} <span className="text-danger">*</span>
                    </label>
                    <select
                      id="gem-region"
                      className={`form-select ${errors.region ? 'is-invalid' : ''}`}
                      value={form.region}
                      onChange={e => handleChange('region', e.target.value)}
                      disabled={!form.country}
                    >
                      <option value="">
                        {form.country
                          ? t('account:info.selectRegion', { region: regionLabel.toLowerCase() })
                          : t('lead.selectCountryFirstArrow')}
                      </option>
                      {regions.map(r => (
                        <option key={r.code} value={r.valueEn}>{r.value}</option>
                      ))}
                    </select>
                    {errors.region && <div className="invalid-feedback">{errors.region}</div>}
                  </div>
                </div>

                {/* City / Postal Code Row */}
                <div className="row mb-3">
                  <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                    <label htmlFor="gem-city" className="form-label fw-semibold">
                      {t('account:info.city')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="gem-city"
                      className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                      value={form.city}
                      onChange={e => handleChange('city', e.target.value)}
                      placeholder={t('lead.placeholders.city')}
                    />
                    {errors.city && <div className="invalid-feedback">{errors.city}</div>}
                  </div>
                  <div className="col-12 col-sm-6">
                    <label htmlFor="gem-postalCode" className="form-label fw-semibold">
                      {t('account:info.postalCode')} <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="gem-postalCode"
                      className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                      value={form.postalCode}
                      onChange={e => handleChange('postalCode', e.target.value)}
                      placeholder={t('lead.placeholders.postalCode')}
                    />
                    {errors.postalCode && <div className="invalid-feedback">{errors.postalCode}</div>}
                  </div>
                </div>
                </>
                )}

                {/* Turnstile Widget */}
                {turnstileActive && (
                  <div className="mb-3">
                    <div ref={turnstileRef} />
                    {errors.turnstileToken && <small className="text-danger">{errors.turnstileToken}</small>}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-danger" onClick={onClose}>
                  {t('auth:cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary d-inline-flex align-items-center gap-1"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="gem-spinner" />
                      {t('save.saving')}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {t('lead.submit')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestEstimateModal;
